import { useIsFocused } from '@react-navigation/native';
import { useSelectedProfile } from 'hooks/useSelectedProfile';
import React, { useEffect } from 'react';
import { Text as NativeBaseText, View } from 'react-native';
import { useAnalytics } from 'services/analytics/useAnalytics';
import theme from 'theme';
import { calcSizeForDevice } from 'utils/index';
import { Platform } from 'utils/platform';

import { useProgram } from './context';

import Button from 'components/Button';
import { Icons } from 'components/Icons';
import { usePlayer } from 'components/Player/context';
import Radio from 'components/Radio/Radio';

type VideoButtonProps = {
  disabled?: boolean;
  mediaIdValue: string | undefined;
  icon: React.ReactNode;
  text: string;
  textColor?: string;
};

type ProgramRadioProps = {
  mediaId?: string;
  openVideo: boolean;
  setMediaId?: (mediaId?: string) => void;
  setOpenVideo?: Function;
};

const ProgramRadio: React.FC<ProgramRadioProps> = ({
  mediaId,
  setMediaId,
  openVideo,
  setOpenVideo,
}) => {
  const program = useProgram();
  const analytics = useAnalytics();
  const isFocused = useIsFocused();
  const { pause, isReady, isFullScreen, isVisible, setFullScreen, setIsVisible } = usePlayer();
  const { isRestrictedProfile } = useSelectedProfile();

  const iconSize = {
    width: calcSizeForDevice(24, 30).toString(),
    height: calcSizeForDevice(24, 30).toString(),
  };

  const handleVideoView = (item: string) => {
    if (!openVideo) pause();
    setMediaId?.(item);
    setOpenVideo?.(!openVideo);

    if (Platform.isTV) {
      setFullScreen(!isFullScreen);
      setIsVisible(!isVisible);
    }
  };

  useEffect(() => {
    if (!isFocused || (Platform.isTV && !isVisible)) {
      setOpenVideo?.(false);
      setMediaId?.(undefined);
    }
  }, [isFocused, isVisible]);

  useEffect(() => {
    if (isFullScreen) {
      setOpenVideo?.(true);
    } else if (isReady && mediaId) {
      setOpenVideo?.(true);
    }
  }, []);

  const VideoButtonComponent: React.FC<VideoButtonProps> = ({
    mediaIdValue,
    icon,
    text,
    disabled = false,
    textColor = '#7A8CFF',
  }) => {
    const onPress = () => {
      const analyticsProgramPayload = {
        programId: program.id,
        programTitle: program.title,
        programAgeRating: program.rating,
        programAdUnit: program.ads?.adunit?.val!,
        programCategory: program.category?.map((tag) => tag.name).join(', '),
        programDirector: program.director,
        programGenre: program.genre.map((genre) => genre.name).join(', '),
        programYear: program.year,
      };

      if (mediaIdValue) analytics.triggerMediaEnableVideoActionEvent(analyticsProgramPayload);
      else analytics.triggerMediaDisableVideoActionEvent(analyticsProgramPayload);
      handleVideoView(mediaIdValue!);
    };

    return (
      <Button variant="ghost" useTVFocus role="link" onPress={onPress} disabled={disabled}>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            marginVertical: calcSizeForDevice(8),
            marginHorizontal: calcSizeForDevice(15),
          }}>
          {icon}
          <NativeBaseText
            style={{
              color: textColor,
              fontFamily: theme.fonts.inter.bold,
              fontSize: calcSizeForDevice(16, 24),
              lineHeight: 22,
              marginLeft: calcSizeForDevice(4),
            }}>
            {` ${text}`}
          </NativeBaseText>
        </View>
      </Button>
    );
  };

  return (
    <View style={{ flex: 1 }}>
      <Radio
        openVideo={openVideo}
        setCloseVideo={() => {
          setMediaId?.(undefined);
          setOpenVideo?.(false);
        }}
        setOpenVideo={setOpenVideo}
        program={program}
        jsxItem={
          program.mediaId != undefined && (
            <View>
              {openVideo ? (
                <VideoButtonComponent
                  text="Desabilitar vídeo"
                  mediaIdValue={undefined}
                  icon={<Icons.VideoSlash color="#7A8CFF" {...iconSize} />}
                />
              ) : (
                <VideoButtonComponent
                  text={
                    !isRestrictedProfile || Platform.isWebTV
                      ? 'Habilitar vídeo'
                      : 'Vídeo desabilitado'
                  }
                  textColor={!isRestrictedProfile || Platform.isWebTV ? '#7A8CFF' : '#f04343'}
                  disabled={Platform.isWebTV ? false : isRestrictedProfile}
                  mediaIdValue={program.mediaId || program.videoId}
                  icon={
                    !isRestrictedProfile || Platform.isWebTV ? (
                      <Icons.Video color="#7A8CFF" {...iconSize} />
                    ) : (
                      <Icons.CircleXmark color="#f04343" {...iconSize} />
                    )
                  }
                />
              )}
            </View>
          )
        }
      />
    </View>
  );
};

export default ProgramRadio;
